import { Comment, CommentQueryParams, RequestPostComment } from '@api/twin/CommentApi';
import { useTeiaViewerContext } from '@stereograph/teiaviewer';
import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePluginTranslation } from '@translation';
import { CommentApi } from '../CommentApi';
import { commentQueryKeys } from '../queryKeys/comment.query-keys';

export const usePostComment = (projectId: number, invalidateQueryKeys: QueryKey) => {
  const { viewer } = useTeiaViewerContext();
  const client = viewer.twinApiClient;
  const queryClient = useQueryClient();
  const { t } = usePluginTranslation();

  const { mutateAsync: postComment } = useMutation<
    Comment,
    Error,
    RequestPostComment,
    string | undefined
  >({
    mutationFn: async (requestPostComment) => {
      return await CommentApi(client, projectId).postComment(requestPostComment);
    },
    onMutate: () => {
      return viewer.notificationCenter.pushNotification({
        type: t('anomaly_plugin.widget.comment_form.notification_title'),
        status: 'pending',
        message: t('anomaly_plugin.widget.comment_form.submit.loading.text'),
        title: t('anomaly_plugin.widget.comment_form.notification_title'),
        displayed: true,
      });
    },
    onSuccess: async (_response, variables, notificationId) => {
      const commentQueryParams = {
        targetId: variables.targetId,
        targetType: variables.targetType,
      } as CommentQueryParams;

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: commentQueryKeys.getAll(projectId, commentQueryParams),
        }),
        queryClient.invalidateQueries({
          queryKey: invalidateQueryKeys,
        }),
      ]);

      viewer.notificationCenter.pushNotification({
        type: t('anomaly_plugin.widget.comment_form.notification_title'),
        status: 'success',
        message: t('anomaly_plugin.widget.comment_form.submit.success.text'),
        title: t('anomaly_plugin.widget.comment_form.notification_title'),
        displayed: true,
      }, notificationId);
    },
    onError: (_error, _variables, notificationId) => {
      viewer.notificationCenter.pushNotification({
        type: t('anomaly_plugin.widget.comment_form.notification_title'),
        status: 'error',
        message: t('anomaly_plugin.widget.comment_form.submit.error.text'),
        title: t('anomaly_plugin.widget.comment_form.notification_title'),
        displayed: true,
      }, notificationId);
    },
  });

  return {
    postComment,
  };
};
